import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress,
  FormControlLabel, Switch, List, ListItem, ListItemText,
  ListItemSecondaryAction, IconButton, Checkbox, Chip, Divider
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Therapist-facing voice whitelist manager.
 *
 * Shows the admin-curated catalog, lets the therapist pick which voices
 * their clients can choose between, reorder the list (displayOrder), and
 * toggle whether clients can pick at all.
 */
const ClientVoiceWhitelist = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [available, setAvailable] = useState([]);
  const [whitelist, setWhitelist] = useState([]); // array of { voiceCatalogId, voice, displayOrder }
  const [allowClientSelection, setAllowClientSelection] = useState(null);

  const authHeaders = { headers: { 'x-auth-token': token } };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [availRes, whitelistRes] = await Promise.all([
        axios.get(`${API_BASE}/therapist/available-voices`, authHeaders),
        axios.get(`${API_BASE}/therapist/voice-whitelist`, authHeaders)
      ]);
      setAvailable(availRes.data?.voices || []);
      setWhitelist(whitelistRes.data?.whitelist || []);
      setAllowClientSelection(whitelistRes.data?.allowClientVoiceSelection ?? null);
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    }
    setLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await axios.put(
        `${API_BASE}/therapist/voice-whitelist`,
        {
          voiceCatalogIds: whitelist.map(e => e.voiceCatalogId),
          allowClientVoiceSelection: allowClientSelection
        },
        authHeaders
      );
      setSuccess('Whitelist saved.');
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    }
    setSaving(false);
  };

  const isInWhitelist = id => whitelist.some(e => String(e.voiceCatalogId) === String(id));

  const toggleVoice = voice => {
    if (isInWhitelist(voice._id)) {
      setWhitelist(whitelist.filter(e => String(e.voiceCatalogId) !== String(voice._id)));
    } else {
      setWhitelist([...whitelist, { voiceCatalogId: voice._id, voice, displayOrder: whitelist.length }]);
    }
  };

  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= whitelist.length) return;
    const next = [...whitelist];
    [next[idx], next[target]] = [next[target], next[idx]];
    setWhitelist(next);
  };

  const previewVoice = async voice => {
    try {
      const providerVoiceId = voice?.providerVoiceId;
      if (!providerVoiceId) return;
      const res = await axios.post(
        `${API_BASE}/tts/generate`,
        {
          text: voice.clientDescription
            ? `Hi — this is ${voice.name}. ${voice.clientDescription}`
            : `Hi — this is ${voice.name}.`,
          voiceId: providerVoiceId
        },
        authHeaders
      );
      if (res.data?.audio) new Audio(`data:audio/mp3;base64,${res.data.audio}`).play();
    } catch (e) {
      setError(`Preview failed: ${e.response?.data?.msg || e.message}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, m: 2 }}>
      <Typography variant="h5" gutterBottom>Client Voice Options</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pick which voices your clients can choose from, and whether they're allowed to choose at all.
        Clients only ever see voices that appear in your whitelist.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Client voice selection permission</Typography>
        <FormControlLabel
          control={<Switch
            checked={allowClientSelection === true}
            indeterminate={allowClientSelection === null}
            onChange={e => setAllowClientSelection(e.target.checked)}
          />}
          label={
            allowClientSelection === true ? 'Clients can pick their voice' :
            allowClientSelection === false ? 'Clients cannot pick their voice' :
            'Inherit platform default'
          }
        />
        {allowClientSelection !== null && (
          <Button size="small" onClick={() => setAllowClientSelection(null)} sx={{ ml: 1 }}>
            Reset to platform default
          </Button>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Your whitelist ({whitelist.length})</Typography>
        <Typography variant="caption" color="text.secondary">Shown to clients in this order.</Typography>
        {whitelist.length === 0 ? (
          <Typography sx={{ mt: 2, color: 'text.secondary' }}>
            No voices selected. Your clients will fall back to the platform-default voice pool.
          </Typography>
        ) : (
          <List dense>
            {whitelist.map((entry, idx) => {
              const voice = entry.voice || available.find(v => v._id === entry.voiceCatalogId) || {};
              return (
                <ListItem key={entry.voiceCatalogId}>
                  <ListItemText
                    primary={voice.name || '(unknown voice)'}
                    secondary={voice.clientDescription || voice.internalDescription || ''}
                  />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => previewVoice(voice)} title="Preview">
                      <PlayCircleOutlineIcon />
                    </IconButton>
                    <IconButton size="small" disabled={idx === 0} onClick={() => move(idx, -1)}>
                      <ArrowUpwardIcon />
                    </IconButton>
                    <IconButton size="small" disabled={idx === whitelist.length - 1} onClick={() => move(idx, 1)}>
                      <ArrowDownwardIcon />
                    </IconButton>
                    <Checkbox
                      checked
                      onChange={() => toggleVoice({ _id: entry.voiceCatalogId })}
                      title="Remove from whitelist"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Available voices ({available.length})</Typography>
        <List dense>
          {available.map(voice => {
            const selected = isInWhitelist(voice._id);
            return (
              <ListItem key={voice._id}>
                <Checkbox
                  edge="start"
                  checked={selected}
                  onChange={() => toggleVoice(voice)}
                />
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <span>{voice.name}</span>
                      {voice.tags?.gender && <Chip size="small" label={voice.tags.gender} />}
                      {voice.tags?.accent && <Chip size="small" label={voice.tags.accent} />}
                    </Box>
                  }
                  secondary={voice.clientDescription || voice.internalDescription || ''}
                />
                <ListItemSecondaryAction>
                  <IconButton size="small" onClick={() => previewVoice(voice)} title="Preview">
                    <PlayCircleOutlineIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      </Paper>

      <Divider sx={{ my: 2 }} />
      <Button variant="contained" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save Whitelist'}
      </Button>
    </Paper>
  );
};

export default ClientVoiceWhitelist;
